import { useMemo, useState } from "react";
import { categories, contestants } from "@/mocks/chigua";
import { catOf, catTone } from "./meta";

type SortKey = "heat" | "seed" | "dark";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "综合热度" },
  { key: "seed", label: "种子优先" },
  { key: "dark", label: "黑马优先" },
];

export default function Roster() {
  const [cat, setCat] = useState("all");
  const [sortBy, setSortBy] = useState<SortKey>("heat");

  const list = useMemo(() => {
    const filtered = cat === "all" ? contestants : contestants.filter((c) => c.category === cat);
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "seed") return Number(b.seed) - Number(a.seed) || b.heat - a.heat;
      if (sortBy === "dark") return Number(b.dark) - Number(a.dark) || b.heat - a.heat;
      return b.heat - a.heat;
    });
    return sorted;
  }, [cat, sortBy]);

  return (
    <section id="roster" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="num-tab mb-3 text-xs tracking-[0.3em] text-primary-600">ROSTER · 参赛名单</p>
            <h2 className="font-heading text-3xl font-bold tracking-wide text-foreground-950 md:text-4xl">
              完整选手盘点<span className="text-primary-600">与热力排行</span>
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              本届共 <strong className="font-semibold text-foreground-900">24 条</strong>候选瓜料入围，覆盖六大晋级赛区。按赛区切换围观，
              热度排序实时可查，看看谁暂列榜首、谁在悄悄逆袭。
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-full bg-background-200/70 p-1">
            {sortOptions.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setSortBy(o.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                  sortBy === o.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCat("all")}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              cat === "all"
                ? "bg-foreground-950 font-medium text-background-50"
                : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:text-foreground-950"
            }`}
          >
            <i className="ri-apps-2-line" />
            全部赛区
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                cat === c.id
                  ? "bg-foreground-950 font-medium text-background-50"
                  : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:text-foreground-950"
              }`}
            >
              <i className={c.icon} />
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((item, i) => {
            const catItem = catOf(item.category);
            return (
              <article
                key={item.id}
                className="group relative flex flex-col rounded-3xl bg-background-50 p-5 ring-1 ring-background-200 transition-all hover:-translate-y-1 hover:ring-primary-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="num-tab flex h-10 w-10 items-center justify-center rounded-xl bg-background-200/70 text-lg text-foreground-900">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] ${catTone(item.category)}`}>
                        <i className={`${catItem.icon} mr-1`} />
                        {catItem.name}
                      </span>
                      {item.seed && (
                        <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[11px] text-background-50">
                          种子选手
                        </span>
                      )}
                      {item.dark && (
                        <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[11px] text-background-50">
                          黑马候选
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {item.name}
                </h3>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-lg bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                    {item.tag}
                  </span>
                  <span className="text-[11px] text-foreground-600">本届热度</span>
                </div>

                <p className="mt-3 line-clamp-2 flex-1 text-sm leading-6 text-foreground-600">{item.summary}</p>

                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-foreground-600">
                      <i className="ri-fire-line text-primary-500" />
                      热力值
                    </span>
                    <span className="num-tab text-base text-primary-600">{item.heat}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-background-200/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500"
                      style={{ width: `${item.heat}%` }}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-foreground-500">
          榜单热力值为综合公开讨论度与票池数据的示意值，实时数据请以瓜友档案馆官网为准。
        </p>
      </div>
    </section>
  );
}